import { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectedStudentTour, setStudentTour } from '../../redux/reducers/studentTourSlice';
import { selectedTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { selectedUnit, setUnit } from '../../redux/reducers/unitSlice';
import { selectActiveStudentFocus } from '../../redux/reducers/focusSlice';
import { isTargetBlocked } from '../../businessLogicService/focusService';
import { StudentSkillStates } from '../../models/StudentSkillState';
import { StudentTour } from '../../models/StudentTour';
import { TargetGroup } from '../../models/TargetGroup';
import { filterWithoutExtensionSkills } from '../../businessLogicService/filterWithoutExtensionSkills';
import { checkTargetGroupMatch } from '../../businessLogicService/matchCurrentTargetGroupInSites';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SearchBox, { SourceItem } from './SearchBox';
import { isSkillCompleted } from '../../businessLogicService/skillCompletionUtils';
import { sortStudentSkillStatesByLabel } from '../../businessLogicService/sortCriteria';
import { sortedByLabel } from '../../models/FilterOptions';
import { useStudentSkillStates } from '../../custom-hooks/useStudentSkillStates';
import { useResolvedUnit } from '../../custom-hooks/useResolvedUnit';
import { Unit } from '../../models/Unit';

interface Props {
  setStudentSkillStates: (s: Array<StudentSkillStates>) => void;
  setSSSWithoutExtension: (s: Array<StudentSkillStates>) => void;
  allTargetsGroup: Array<TargetGroup>;
  currentTargets: ReadonlyArray<{
    id: string;
    name?: string | null;
    summary?: string | null;
  }>;
}

const DashboardHeader: FC<Props> = ({
  setStudentSkillStates,
  allTargetsGroup,
  setSSSWithoutExtension,
  currentTargets,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const tourMode = useAppSelector(selectedStudentTour);
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const unitRedux = useAppSelector(selectedUnit);
  const activeFocus = useAppSelector(selectActiveStudentFocus);
  // Auto-select default unit when studentSkillStates are available and no unit is selected
  const unitName = searchParams.get('unitNumber');
  const unitsInTargetGroup = currentTargets as Unit[]; // show only units in current target group
  const matchingUnit = unitsInTargetGroup?.find(unit => unit.name === unitName);

  // Focus-aware current unit: focus while incomplete, else the progress default.
  // An explicit ?unitNumber= (student browsing elsewhere) still wins below.
  const resolvedUnit = useResolvedUnit(unitsInTargetGroup);
  const { studentSkillStates } = useStudentSkillStates({ includeExtensions: true });
  // Note: Includes extension skills, but filters for the current target_group/roadmap.

  const sortedFilteredStudentSkillStates = sortStudentSkillStatesByLabel(
    studentSkillStates?.filter(skillState => {
      return skillState.skill.targetSkills?.some(
        targetSkill => targetSkill.target?.targetGroup?.id === targetGroupRedux?.id,
      );
    }),
    sortedByLabel.READINESS_SCORE,
  );

  const searchableSkillData: SourceItem[] = sortedFilteredStudentSkillStates.map(skillState => ({
    id: skillState.skill.id,
    code: skillState.skill.code,
    searchKey: `${skillState.skill.code}: ${skillState.skill.name}`,
    node: (
      <div>
        {isSkillCompleted(skillState) ? '✅ ' : ''}
        {skillState.skill.code}: {skillState.skill.name}
      </div>
    ),
  }));

  // Set tour mode based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    let targetTourMode = StudentTour.UNITS; // default

    if (currentPath.includes('/pick-unit')) {
      targetTourMode = StudentTour.UNIT_SELECTION;
    } else {
      targetTourMode = StudentTour.UNITS;
    }

    if (tourMode !== targetTourMode) {
      dispatch(setStudentTour(targetTourMode));
    }
  }, [location.pathname, tourMode, dispatch]);

  useEffect(() => {
    if (checkTargetGroupMatch(allTargetsGroup, targetGroupRedux as TargetGroup)) {
      let sss: StudentSkillStates[] = [];
      sss = studentSkillStates as StudentSkillStates[];
      setSSSWithoutExtension(filterWithoutExtensionSkills(sss, targetGroupRedux));
      setStudentSkillStates(sss);
    }
  }, []);

  useEffect(() => {
    // Blocked ?unitNumber= must not strand the student: strip it, let focus resolution take over.
    if (unitName && matchingUnit && isTargetBlocked(matchingUnit.id, activeFocus)) {
      navigate('/', { replace: true });
      return;
    }
    const landingUnit = unitName ? matchingUnit : resolvedUnit;
    if (landingUnit && landingUnit.name !== unitRedux?.name) {
      dispatch(setUnit(landingUnit));
    }
  }, [unitName, resolvedUnit, unitRedux, matchingUnit, activeFocus, navigate, dispatch]);

  return (
    <>
      {studentSkillStates && (
        <div className="col-10 col-offset-1 flex flex-wrap justify-content-between align-items-center gap-lg">
          {/* Add a link based on the tour mode */}
          <div className="tour-link-container">
            {tourMode === StudentTour.UNITS ? ( // Within a unit.
              <Link
                to={'./pick-unit'}
                className="tw-text-blue-500 tw-font-atkinson tw-text-base tw-font-bold tw-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0
             focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#0C1B54] focus-visible:tw-ring-offset-2 focus-visible:tw-rounded-md"
              >
                <ArrowLeft className="tw-h-4 tw-rounded-full tw-inline-flex tw-justify-center tw-items-center tw-gap-2 tw-overflow-hidden" />
                See all units
              </Link>
            ) : (
              // Picking a unit.
              <Link
                to={unitRedux ? '../?unitNumber=' + unitRedux?.name : '/'}
                className="tw-text-blue-500 tw-flex tw-items-center tw-font-atkinson tw-text-base tw-font-bold tw-gap-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 hover:tw-text-[#0C1B54]
             focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#0C1B54] focus-visible:tw-ring-offset-2 focus-visible:tw-rounded-md"
              >
                <ArrowLeft className="tw-h-4 tw-rounded-full tw-flex  tw-justify-center tw-items-center tw-gap-2 tw-overflow-hidden" />
                Return
              </Link>
            )}
          </div>
          {targetGroupRedux?.id ? (
            <div className="flex justify-content-end align-items-center gap-lg">
              <SearchBox
                items={searchableSkillData}
                onSelect={skillCode => {
                  navigate(`/skill/${skillCode}`);
                }}
              />
            </div>
          ) : (
            <h1 className="w-full text-center h-13rem">Choose a Roadmap above to get started!</h1>
          )}
        </div>
      )}
    </>
  );
};

export default DashboardHeader;
